/**
 * MISSION: Which engine answers a turn, and what the browser already heard.
 * CONTEXT_ANCHORS: [sparkly.service.ts], [voicelab-ai/index.ts]
 *
 * Two module-level slots the host sets and `sendAudio` reads. They live here
 * rather than in `sparkly.service.ts` because that file's own header says it
 * exists to keep the STATEFUL session operations together — `conversationBuffer`,
 * `feedbackBuffer`, `weakAreasCache` — and these two share nothing with them.
 * They are configuration, set once and read once, and `check-file-size` was
 * right to push them out.
 *
 * Why slots at all: `sendAudio` is a loose function with no view of React state,
 * and threading either of these through its signature would change a call the
 * child front also makes.
 */

/**
 * The Edge Function that answers a turn. Both speak the same contract, so this
 * is a URL and nothing else.
 *
 * A SETTER, because this package has two consumers and they are two products:
 * `apps/legacy` stays on `sparkly-process` (Gemini, Spanish, its streak), and
 * `apps/student` runs `voicelab-ai` (OpenAI end to end). An env var here would
 * pick one for both. The default keeps today's behaviour, and is the rollback.
 */
let speakingEngine = 'sparkly-process';

export function setSpeakingEngine(slug: 'sparkly-process' | 'voicelab-ai'): void {
    speakingEngine = slug;
}

export function currentSpeakingEngine(): string {
    return speakingEngine;
}

/**
 * What the browser already heard. `voicelab-ai` transcribes server-side (~900ms)
 * so it works on any device; where `SpeechRecognition` exists it produced the
 * same words for free while the student was still speaking, so the front hands
 * them over and the server skips the step. Empty means "you transcribe it" —
 * what Safari and every device without recognition sends.
 */
let pendingTranscript = '';

export function setPendingTranscript(text: string): void {
    pendingTranscript = text.trim();
}

/**
 * Read once and cleared in the same call. A transcript that survived a failed
 * turn would be attached to the next one, and the student would be answered for
 * something they said a minute ago.
 */
export function takePendingTranscript(): string {
    const text = pendingTranscript;
    pendingTranscript = '';
    return text;
}
